import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Sparkles, X } from 'lucide-react';
import { billingApi } from '@/lib/billing';

const DISMISS_KEY = 'cadencez.trialBanner.dismissed';

/**
 * Countdown banner shown while a company is on its 30-day reverse-trial.
 * Mirrors UnverifiedEmailBanner (session-dismissible). Reads trial state off
 * the workspace's company; renders nothing once the trial ends.
 */
export function TrialBanner({ wsId, companyId }: { wsId?: string; companyId?: string }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState<boolean>(
    () => typeof window !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1',
  );
  const { data: trial } = useQuery({
    queryKey: ['billing-trial', companyId],
    queryFn: () => billingApi.trial(companyId!),
    enabled: !!companyId,
    staleTime: 60_000,
  });

  if (dismissed || !wsId || !trial?.onTrial) return null;

  const onDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  return (
    <div className="bg-accent-50 border-b border-accent-200">
      <div className="px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 text-ink-900">
          <Sparkles className="w-4 h-4 text-accent-600 flex-shrink-0" />
          <span>
            <Trans
              i18nKey="common.trial_banner"
              values={{ count: trial.daysLeft }}
              components={[<strong key="d" />]}
              defaults="Your free trial ends in <0>{{count}} days</0> — upgrade to keep Growth features."
            />
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to={`/app/workspace/${wsId}/subscription`}
            className="inline-flex items-center px-3 py-1 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-xs font-semibold"
          >
            {t('common.upgrade', { defaultValue: 'Upgrade' })}
          </Link>
          <button
            onClick={onDismiss}
            className="p-1 rounded-md text-accent-700 hover:bg-accent-100"
            aria-label={t('common.dismiss', { defaultValue: 'Dismiss' })}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
